import { computed } from 'mobx';
import $dictionary from 'app/stores/$dictionary';
import $devices from 'app/stores/device/$devices';
import {
  MITIGATION_TYPE_LABELS,
  MITIGATION_TYPES,
  BGP_ANNOUNCEMENT_ACTION_LABELS,
  BGP_ANNOUNCEMENT_MATCH_LABELS
} from '@kentik/ui-shared/mitigations/constants';
import Model from 'core/model/Model';
import { formatDateTime } from 'core/util/dateUtils';

const ACTION_VALUE_LABELS = {
  ipv4: 'IPv4',
  ipv6: 'IPv6'
};

const OPERATOR_SINGLE_LABELS = {
  BITWISE_OP_ANY: '',
  BITWISE_OP_NONE: 'none: ',
  BITWISE_OP_ALL: '',
  BITWISE_OP_NOT_ALL: 'not: ',
  NUMERIC_OP_EQUALS: '',
  NUMERIC_OP_NOT_EQUALS: '≠',
  NUMERIC_OP_LESS_THAN: '<',
  NUMERIC_OP_GREATER_THAN: '>',
  NUMERIC_OP_LESS_THAN_OR_EQUALS: '>=',
  NUMERIC_OP_GREATER_THAN_OR_EQUALS: '<='
};

const OPERATOR_MULTI_LABELS = {
  ...OPERATOR_SINGLE_LABELS,
  BITWISE_OP_ANY: 'any: ',
  BITWISE_OP_NONE: 'none: ',
  BITWISE_OP_ALL: 'all: '
};

const MATCH_KEY_TO_DICTIONARY_KEY = { tcpFlags: 'tcp_flags', ipProtocol: 'protocol' };

export default class BGPAnnouncementModel extends Model {
  get urlRoot() {
    return '/api/ui/mitigations/announcements';
  }

  get deviceId() {
    return this.get('deviceId', null);
  }

  get deviceName() {
    const { device_name } = $devices.deviceSummariesById[this.deviceId] || {};
    return device_name;
  }

  get deviceSessionStatus() {
    const deviceStatusMatch = $devices.deviceStatusById[this.deviceId];

    if (!deviceStatusMatch) {
      return null;
    }

    if (deviceStatusMatch.metadata?.device_bgp_neighbor_ip || deviceStatusMatch.metadata?.device_bgp_neighbor_ip6) {
      return 'Up';
    }

    return 'Down';
  }

  get sortValues() {
    return {
      creationTime: () => this.creationTime
    };
  }

  @computed
  get isAdaptiveFlowspecAnnouncement() {
    return (this.typeDetails?.key || '').startsWith('mit2bgp-');
  }

  @computed
  get typeKey() {
    const attributes = this.get();
    const attributeKeys = Object.keys(attributes);
    const supportedMitigationTypes = [MITIGATION_TYPES.RTBH, MITIGATION_TYPES.FLOWSPEC];

    return attributeKeys.find((key) => supportedMitigationTypes.includes(key));
  }

  @computed
  get typeLabel() {
    if (this.isAdaptiveFlowspecAnnouncement) {
      return MITIGATION_TYPE_LABELS[MITIGATION_TYPES.ADAPTIVE_FLOWSPEC];
    }

    return MITIGATION_TYPE_LABELS[this.typeKey] || 'Undefined';
  }

  @computed
  get typeDetails() {
    return this.get(this.typeKey, {});
  }

  @computed
  get creationTime() {
    return formatDateTime(this.typeDetails.creationTime);
  }

  @computed
  get matchBody() {
    return this.typeDetails.match || {};
  }

  @computed
  get actionLabels() {
    return BGP_ANNOUNCEMENT_ACTION_LABELS[this.typeKey] || {};
  }

  @computed
  get matchLabels() {
    return BGP_ANNOUNCEMENT_MATCH_LABELS[this.typeKey] || {};
  }

  @computed
  get actionBody() {
    if (this.typeKey === MITIGATION_TYPES.RTBH) {
      return this.typeDetails.action;
    }

    if (this.typeKey === MITIGATION_TYPES.FLOWSPEC) {
      const actions = this.typeDetails?.actions || [];

      return actions.reduce((acc, curr) => ({ ...acc, ...curr }), {});
    }

    return {};
  }

  formatActionValue = (actionValue) => {
    if (actionValue === null) {
      return actionValue;
    }

    if (typeof actionValue === 'object') {
      // Some actions have nested objects, we need to extract the value
      const objectKey = Object.keys(actionValue)[0];
      const objectValue = actionValue[objectKey];

      // If we're missing anything, return null
      if (!objectValue) {
        return 'true';
      }

      // If there's a nested object here, just stringify it.
      if (typeof objectValue === 'object') {
        return JSON.stringify(objectValue);
      }

      return $dictionary.get(`queryFilters.options.${objectKey}.${objectValue}`, objectValue);
    }

    return ACTION_VALUE_LABELS[actionValue] || actionValue;
  };

  @computed
  get actionEntries() {
    const actionKeys = Object.keys(this.actionBody);

    const actionEntries = [];

    actionKeys.forEach((key) => {
      const label = this.actionLabels[key];
      const value = this.formatActionValue(this.actionBody[key]);

      if (label && value !== null) {
        actionEntries.push({ label, value, key });
      }
    });

    return actionEntries;
  }

  @computed
  get actionEntriesString() {
    return this.actionEntries.map((entry) => `${entry.label}: ${entry.value}`).join(' | ');
  }

  formatMatchValues = (matchValue, matchKey) => {
    if (matchValue === null || matchValue === '') {
      return null;
    }

    if (typeof matchValue !== 'object') {
      // Fallback to the original if not found, even if it's null
      return [$dictionary.get(`queryFilters.options.${matchKey}.${matchValue}`, matchValue)];
    }

    /**
     * This is going to follow an OR/AND nested structure for boolean matches.
     * The structure is as follows:
     * {
     *   "or": [
     *    { "and": [{ "operator": "OP", "values": [] }] },
     *    { "and": [{ "operator": "OP", "value": "string" }] },
     *   ]
     * }
     */
    if (typeof matchValue === 'object' && matchValue.or && Array.isArray(matchValue.or)) {
      const orGroups = matchValue.or;
      const andGroups = orGroups.map((orGroup) => orGroup.and).flat();

      return andGroups.map((andGroup) => {
        const andValues = andGroup.values ? andGroup.values : [andGroup.value];
        const displayValues = andValues.map((andValue) => {
          const dictionaryKey = MATCH_KEY_TO_DICTIONARY_KEY[matchKey] || matchKey;
          return $dictionary.get(`queryFilters.options.${dictionaryKey}.${andValue}`, andValue);
        });
        const operatorMap = displayValues.length === 1 ? OPERATOR_SINGLE_LABELS : OPERATOR_MULTI_LABELS;
        const andOperator = operatorMap[andGroup.operator] || '';

        return `${andOperator}${displayValues.join(', ')}`;
      });
    }

    // If this is an unexpected object structure, just stringify it to avoid breaking
    return [JSON.stringify(matchValue)];
  };

  @computed
  get matchEntries() {
    const matchKeys = Object.keys(this.matchBody);
    const matchEntries = [];

    matchKeys.forEach((matchKey) => {
      const label = this.matchLabels[matchKey];
      const values = this.formatMatchValues(this.matchBody[matchKey], matchKey);

      if (label && values !== null) {
        matchEntries.push({ label, values, key: matchKey });
      }
    });

    return matchEntries;
  }

  @computed
  get matchEntriesString() {
    return this.matchEntries.map((entry) => `${entry.label}: ${entry.values.join(', ')}`).join(' | ');
  }

  get mitigationIds() {
    return this.get('fullRouteMatches', []);
  }

  get unappliedMitigationIds() {
    return this.get('filterOnlyMatches', []);
  }

  get mitigationIdsString() {
    return this.mitigationIds.join(', ');
  }
}
